import { getErrorMessage } from '@repo/lib'
import * as Sentry from '@sentry/browser'

import { useDialogContext } from '@/dialogs/DialogContext'
import { useGameContext } from '@/pages/game/context/game-context'

import { SpotifyErrorMessage } from '../types'
import { useSpotify } from '../useSpotify'

interface UseSpotifyPlaybackErrorOptions {
  onError?: () => void
  setShowDeviceSelectorModal: (show: boolean) => void
}

export function useSpotifyPlaybackError({
  onError,
  setShowDeviceSelectorModal
}: UseSpotifyPlaybackErrorOptions) {
  const { setErrorDialog } = useDialogContext()
  const { tracker } = useGameContext()
  const { deviceService } = useSpotify()

  // Normalize error message for comparison (trim whitespace, lowercase)
  const normalizeErrorMessage = (message: string): string => {
    return message.trim().toLowerCase()
  }

  // Check if error message matches any of the expected Spotify error messages
  const matchesErrorMessage = (errorMessage: string, expectedMessages: string[]): boolean => {
    const normalized = normalizeErrorMessage(errorMessage)
    return expectedMessages.some(expected => normalizeErrorMessage(expected) === normalized)
  }

  const handlePlaybackError = (error: unknown) => {
    onError?.()

    const errorMessage = getErrorMessage(error)

    // Handle restriction violated errors (player already in desired state)
    // This is not a real error - silently ignore it
    if (matchesErrorMessage(errorMessage, [SpotifyErrorMessage.RESTRICTION_VIOLATED])) {
      // Player is already in the desired state, no action needed
      return
    }

    // Handle device-related errors (not found, device not found, bad gateway, server error)
    if (
      matchesErrorMessage(errorMessage, [
        SpotifyErrorMessage.NOT_FOUND,
        SpotifyErrorMessage.DEVICE_NOT_FOUND,
        SpotifyErrorMessage.BAD_GATEWAY,
        SpotifyErrorMessage.SERVER_ERROR
      ])
    ) {
      tracker.spotifyDeviceError()

      void deviceService.queryDevices()

      setShowDeviceSelectorModal(true)
      return
    }

    if (matchesErrorMessage(errorMessage, [SpotifyErrorMessage.INVALID_AUTH_TOKEN])) {
      setShowDeviceSelectorModal(true)
      return
    }

    if (matchesErrorMessage(errorMessage, [SpotifyErrorMessage.PREMIUM_REQUIRED])) {
      tracker.spotifyPremiumError()

      setErrorDialog({
        error: 'Spotify Premium account is required to control playback'
      })

      return
    }

    if (matchesErrorMessage(errorMessage, [SpotifyErrorMessage.INVALID_TRACK])) {
      tracker.spotifyTrackError()

      setErrorDialog({
        error: 'There was a problem playing this song. Please skip ahead.'
      })

      return
    }

    setErrorDialog({ error: 'There was a problem connecting to Spotify.' })
  }

  const withErrorHandling = async (action: () => Promise<void>) => {
    try {
      return await action()
    } catch (error) {
      const errorMessage = getErrorMessage(error)
      
      // Don't report restriction violated errors to Sentry - they're not real errors
      const isRestrictionViolated = matchesErrorMessage(errorMessage, [
        SpotifyErrorMessage.RESTRICTION_VIOLATED
      ])
      
      handlePlaybackError(error)
      
      // Only capture non-restriction errors to Sentry
      if (!isRestrictionViolated) {
        Sentry.captureException(error)
      }
    }
  }

  return {
    withErrorHandling
  }
}
